import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { useRedeemPointsLink } from "@/hooks/useRedeemPointsLink";
import { tryGetAuthenticatedUser } from "@/store/auth.store";
import { useGetBanners } from "@roman/shared/hooks/banner/useGetBanners";
import type { Banner } from "@roman/shared/types/Banner";
import { cn } from "@roman/shared/utils";
import Autoplay from "embla-carousel-autoplay";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const PromotionBanners = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { onClick: onClickRedeemPointsLink } = useRedeemPointsLink({
    spram1: "campaignbanner",
  });
  const { banners, isLoading } = useGetBanners();
  const location = useLocation();
  const user = tryGetAuthenticatedUser();
  const navigate = useNavigate();

  const orderedBanners = banners ? [...banners].sort((a, b) => a.order - b.order) : [];

  useEffect(() => {
    if (!api) {
      return;
    }

    api.on("select", () => {
      setSelectedIndex(api.selectedScrollSnap());
    });
  }, [api]);

  const handleClickBanner = (banner: Banner) => {
    if (banner.isExternal) {
      window.open(banner.targetLink, "_blank");
      return;
    }

    if (!user) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    onClickRedeemPointsLink();
  };

  if (isLoading || orderedBanners.length === 0) return;

  return (
    <div className="w-full">
      <Carousel
        setApi={setApi}
        className="w-full"
        opts={{
          loop: true,
        }}
        plugins={[
          Autoplay({
            delay: 3000,
            stopOnInteraction: false,
          }),
        ]}
      >
        <CarouselContent>
          {orderedBanners.map((banner) => (
            <CarouselItem key={banner.id} className="w-full">
              <img
                src={banner.bannerUrl}
                alt="promotion"
                className="w-full h-full cursor-pointer object-contain"
                draggable={false}
                onClick={() => handleClickBanner(banner)}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="py-2 flex space-x-[13px] justify-center">
        {orderedBanners.map((banner, index) => (
          <div
            key={banner.id}
            className={cn(
              "w-[8px] h-[4px] sm:w-[14px] lg:w-[14px] rounded-full bg-disabled cursor-pointer",
              selectedIndex === index && "bg-primary",
            )}
            onClick={() => {
              api?.scrollTo(index);
            }}
          />
        ))}
      </div>
    </div>
  );
};
